/* paySchedule.js -- THE PAY CALENDAR (bi-weekly payroll, step 3a, 2026-10-08).
 *
 * PURE: no I/O, no Date.now, no module state. It imports NOTHING from the project; salarySeries.js imports it.
 * It owns five things and nothing else:
 *   1. date arithmetic on 'YYYY-MM-DD' strings (split('-') + Date.UTC -- never new Date('YYYY-MM-DD'), CLAUDE.md section 5)
 *   2. the bank-holiday rule (Brian Q3, 2026-10-08)
 *   3. the pay periods of a bi-weekly schedule (14-day periods counted from one real check date)
 *   4. the pay-schedule answer's own rules: what is required when, which accounts may carry the accrual, which day a pay
 *      change may take effect on
 *   5. the ONE mapper from the settings row (snake_case, as the server returns it) to the engine's schedule object
 *
 * THE WALL: server/server.js cannot import this file, so items 2 and 4 are spelled a second time there (the "PAY SCHEDULE"
 * block). verify-wall-mirrors.js holds the two equal: M15 (validatePaySchedule . accruedLeafKeysFromMeta . bankHolidayName /
 * isBankBusinessDay) and M16 (raiseDayAllowed). A change here without the same change there fails the client prebuild.
 *
 * THE ENGINE-SIDE SCHEDULE: { frequency, firstCheckDate, lagDays, books, accrualCovers }
 *   frequency      'semi_monthly' | 'biweekly' | null (never answered -- READ as semi-monthly, Brian B2)
 *   firstCheckDate 'YYYY-MM-DD', one real check date (the anchor); bi-weekly only
 *   lagDays        whole days from a pay period's last day to its check date, 0..14; bi-weekly only
 *   books          'accrues' | 'when_paid'; bi-weekly only
 *   accrualCovers  'wages' | 'wages_and_taxes'; accrues only
 * Design: diffs/biweekly-design.txt sections 2 and 5, as amended by diffs/biweekly-design-amend.txt 4.2.
 * Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). */

export const PAY_FREQUENCIES = ['semi_monthly', 'biweekly'];
export const PAYROLL_BOOKS = ['accrues', 'when_paid'];
export const PAYROLL_ACCRUAL_COVERS = ['wages', 'wages_and_taxes'];
export const PAY_LAG_DAYS_MIN = 0;
export const PAY_LAG_DAYS_MAX = 14;
export const PAY_SCHEDULE_COLUMNS = ['pay_frequency', 'pay_first_check_date', 'pay_lag_days', 'payroll_books', 'payroll_accrual_covers', 'accrued_payroll_bs_line_key'];
export const PAY_PERIOD_DAYS = 14;

const DAY_MS = 86400000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/* ---------------------------------------------------------------- dates ---------------------------------------------------------------- */
/* UTC days are always 86,400,000 ms apart, so no daylight-saving step can move a date. Every date is carried as an ISO
   string; ISO strings compare correctly with < and <=. */

// The date's UTC milliseconds, or null when iso is not a REAL calendar date ('2026-02-30' -> null).
export function isoDateT(iso) {
  if (typeof iso !== 'string' || !ISO_DATE.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() + 1 !== m || back.getUTCDate() !== d) return null;
  return t;
}
function isoOf(y, m, d) {
  return String(y).padStart(4, '0') + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}
export function addDays(iso, days) {
  const x = new Date(isoDateT(iso) + days * DAY_MS);
  return isoOf(x.getUTCFullYear(), x.getUTCMonth() + 1, x.getUTCDate());
}
// 0 = Sunday .. 6 = Saturday
export function weekdayOf(iso) {
  return new Date(isoDateT(iso)).getUTCDay();
}
// The first / last day of a 'YYYY-MM' month.
export function monthStartIso(ym) {
  return ym + '-01';
}
export function monthEndIso(ym) {
  const [y, m] = ym.split('-').map(Number);
  return isoOf(y, m, new Date(Date.UTC(y, m, 0)).getUTCDate());
}
function nthWeekday(y, m, weekday, n) {   // the n-th <weekday> of month m
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  return isoOf(y, m, 1 + ((weekday - first + 7) % 7) + 7 * (n - 1));
}
function lastWeekday(y, m, weekday) {   // the last <weekday> of month m
  const last = new Date(Date.UTC(y, m, 0));
  return isoOf(y, m, last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7));
}

/* ----------------------------------------------------------- the bank-holiday rule ----------------------------------------------------------- */
/* Brian Q3 (2026-10-08). The eleven holidays are COMPUTED, not a typed list. For the five fixed-date ones: on a SUNDAY the
   holiday is observed the following MONDAY; on a SATURDAY it is observed the preceding FRIDAY (the payroll provider's own
   behaviour: the 2026-07-03 check was paid 2026-07-02). A Saturday January 1 is therefore observed on December 31 of the
   YEAR BEFORE, so the fixed-date holidays of the date's own year AND of the next year are both consulted (first case:
   Friday 2027-12-31).
   bankHolidayName(iso): the holiday's name (with " (observed)" when it is not the calendar date itself), or null.
   WALL MIRROR M15: server.js bankHolidayName / isBankBusinessDay. */
const BANK_FIXED_HOLIDAYS = [[1, 1, "New Year's Day"], [6, 19, 'Juneteenth National Independence Day'], [7, 4, 'Independence Day'], [11, 11, 'Veterans Day'], [12, 25, 'Christmas Day']];

export function bankHolidayName(iso) {
  if (isoDateT(iso) === null) return null;
  const y = Number(iso.slice(0, 4));
  for (const year of [y, y + 1]) {
    for (const [m, d, name] of BANK_FIXED_HOLIDAYS) {
      const h = isoOf(year, m, d);
      if (isoDateT(h) === null) continue;
      const w = weekdayOf(h);
      if (w === 0) { if (iso === addDays(h, 1)) return name + ' (observed)'; }
      else if (w === 6) { if (iso === addDays(h, -1)) return name + ' (observed)'; }
      else if (iso === h) return name;
    }
  }
  if (iso === nthWeekday(y, 1, 1, 3)) return 'Birthday of Martin Luther King, Jr.';
  if (iso === nthWeekday(y, 2, 1, 3)) return "Washington's Birthday";
  if (iso === lastWeekday(y, 5, 1)) return 'Memorial Day';
  if (iso === nthWeekday(y, 9, 1, 1)) return 'Labor Day';
  if (iso === nthWeekday(y, 10, 1, 2)) return 'Columbus Day';
  if (iso === nthWeekday(y, 11, 4, 4)) return 'Thanksgiving Day';
  return null;
}
// A real date that is not a weekend and not an observed holiday.
export function isBankBusinessDay(iso) {
  if (isoDateT(iso) === null) return false;
  const w = weekdayOf(iso);
  return w !== 0 && w !== 6 && bankHolidayName(iso) === null;
}

/* ------------------------------------------------------------- the schedule object ------------------------------------------------------------- */
// THE ONE MAPPER: the settings row (GET /api/companies/:companyId/settings) -> the engine's schedule. A row that was never
// answered maps to a schedule whose frequency is null, which every reader treats as semi-monthly. No row -> null.
export function payScheduleFromSettings(row) {
  if (!row || typeof row !== 'object') return null;
  const v = k => (row[k] === undefined || row[k] === null ? null : row[k]);
  return {
    frequency: v('pay_frequency'),
    firstCheckDate: v('pay_first_check_date'),
    lagDays: v('pay_lag_days'),
    books: v('payroll_books'),
    accrualCovers: v('payroll_accrual_covers'),
  };
}
// Nothing, null, a never-answered schedule and 'semi_monthly' are all NOT bi-weekly.
export function isBiweekly(schedule) {
  return !!schedule && schedule.frequency === 'biweekly';
}
// Why a bi-weekly schedule cannot produce a calendar, in words -- or null when it can. (The database's CHECK constraints
// and the pay-schedule route refuse these states; this names one that arrived any other way.)
export function payCalendarProblem(schedule) {
  if (!isBiweekly(schedule)) return 'the schedule is not bi-weekly';
  if (isoDateT(schedule.firstCheckDate) === null) return 'a bi-weekly schedule needs a real first check date (YYYY-MM-DD) -- got ' + JSON.stringify(schedule.firstCheckDate);
  const lag = schedule.lagDays;
  if (typeof lag !== 'number' || !Number.isInteger(lag) || lag < PAY_LAG_DAYS_MIN || lag > PAY_LAG_DAYS_MAX)
    return 'a bi-weekly schedule needs a whole number of days from period end to check date, ' + PAY_LAG_DAYS_MIN + ' to ' + PAY_LAG_DAYS_MAX + ' -- got ' + JSON.stringify(lag);
  return null;
}
function calendarOf(schedule) {
  const problem = payCalendarProblem(schedule);
  if (problem) throw new Error('paySchedule: ' + problem);
  return { anchor: schedule.firstCheckDate, lag: schedule.lagDays };
}

/* --------------------------------------------------------------- the pay periods --------------------------------------------------------------- */
/* Period k (any integer, negative included -- the calendar extends both ways from the anchor without limit):
     nominalCheck = the first check date + 14 x k
     end          = nominalCheck - the lag            (the lag is measured to the NOMINAL check date)
     start        = end - 13                          (14 days, inclusive)
     check        = nominalCheck, moved to the prior business day while it is a weekend or an observed holiday
   A holiday moves the CHECK, never the period. Every period is 14 days, so a full check is annual / 26. */
export function payPeriod(schedule, k) {
  const { anchor, lag } = calendarOf(schedule);
  const nominalCheck = addDays(anchor, PAY_PERIOD_DAYS * k);
  const end = addDays(nominalCheck, -lag);
  const start = addDays(end, -(PAY_PERIOD_DAYS - 1));
  let check = nominalCheck;
  let reason = '';
  while (!isBankBusinessDay(check)) {
    if (reason === '') reason = bankHolidayName(check) || WEEKDAY_NAMES[weekdayOf(check)];
    check = addDays(check, -1);
  }
  return { k, start, end, nominalCheck, check, moved: check !== nominalCheck, reason };
}
// k of the period that contains a day.
export function periodIndexOf(schedule, iso) {
  const { anchor, lag } = calendarOf(schedule);
  const t = isoDateT(iso);
  if (t === null) throw new Error('paySchedule: ' + JSON.stringify(iso) + ' is not a real calendar date');
  const start0 = isoDateT(anchor) - (lag + PAY_PERIOD_DAYS - 1) * DAY_MS;
  return Math.floor((t - start0) / (PAY_PERIOD_DAYS * DAY_MS));
}
/* Every period that OVERLAPS [a, b] or is PAID in [a, b], in order.
   Bounds: a period paid on or after `a` has its nominal check on or after `a`, so its end is no earlier than a - 14; and a
   period that starts after `b`'s own period ends is paid after `b` (a check moves back a few days at most, never 13). The
   exact test is then applied to each candidate. */
export function periodsBetween(schedule, a, b) {
  if (isoDateT(a) === null || isoDateT(b) === null) throw new Error('paySchedule: periodsBetween needs two real calendar dates');
  const out = [];
  if (b < a) return out;
  const k0 = periodIndexOf(schedule, addDays(a, -PAY_PERIOD_DAYS));
  const k1 = periodIndexOf(schedule, b) + 1;
  for (let k = k0; k <= k1; k++) {
    const p = payPeriod(schedule, k);
    const overlaps = p.start <= b && p.end >= a;
    const paidInside = p.check >= a && p.check <= b;
    if (overlaps || paidInside) out.push(p);
  }
  return out;
}
// The END of the last period whose CHECK date is on or before a day -- everything earned after it is unpaid on that day.
export function lastPaidPeriodEnd(schedule, iso) {
  let k = periodIndexOf(schedule, iso) + 1;   // the period after the day's own: its check is always after the day
  while (payPeriod(schedule, k).check > iso) k -= 1;
  return payPeriod(schedule, k).end;
}
// { 'YYYY-MM': [check dates, in order] } for each month asked.
export function checksByMonth(schedule, months) {
  const out = {};
  for (const ym of months || []) {
    const a = monthStartIso(ym);
    const b = monthEndIso(ym);
    out[ym] = periodsBetween(schedule, a, b).filter(p => p.check >= a && p.check <= b).map(p => p.check);
  }
  return out;
}

/* ----------------------------------------------------------- the pay-schedule answer ----------------------------------------------------------- */
/* THE WHOLE-SET RULE -- pure: what is required when. Takes the six values under their settings (snake_case) names, exactly
   as the screen sends them to PUT /api/companies/:companyId/pay-schedule. Returns { refusal: { error, code } } or { value }
   where value carries all six, a field that does not apply as null. "Given" = present and not null; '' is a value, and is
   refused.
     semi_monthly -> the other five must not be given
     biweekly     -> a first check date (YYYY-MM-DD, a real date, a bank business day), a lag (a whole number 0..14), books
     accrues      -> what the accrual covers; the account is optional here (it exists only after Step 4; whether it is one of
                     this company's accrued lines is accruedLeafKeysFromMeta's question, below)
     when_paid    -> covers and the account must not be given
   WALL MIRROR M15: server.js validatePaySchedule -- same codes, same messages, same value. */
export function validatePaySchedule(values) {
  const b = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const given = k => b[k] !== undefined && b[k] !== null;
  const refuse = (code, error) => ({ refusal: { error, code } });
  const value = { pay_frequency: null, pay_first_check_date: null, pay_lag_days: null, payroll_books: null, payroll_accrual_covers: null, accrued_payroll_bs_line_key: null };
  if (!given('pay_frequency')) return refuse('PAY_FREQUENCY_REQUIRED', 'Choose how payroll is paid: semi-monthly or bi-weekly.');
  if (!PAY_FREQUENCIES.includes(b.pay_frequency)) return refuse('PAY_FREQUENCY_INVALID', 'Pay frequency must be semi_monthly or biweekly.');
  value.pay_frequency = b.pay_frequency;
  if (b.pay_frequency === 'semi_monthly') {
    const extra = PAY_SCHEDULE_COLUMNS.slice(1).filter(given);
    if (extra.length) return refuse('SEMI_MONTHLY_EXTRA_FIELDS', 'A semi-monthly company has no ' + extra.join(', ') + ' -- leave ' + (extra.length === 1 ? 'it' : 'them') + ' blank.');
    return { value };
  }
  if (!given('pay_first_check_date')) return refuse('FIRST_CHECK_DATE_REQUIRED', 'Enter one real check date. A bi-weekly schedule is counted from it.');
  const d = b.pay_first_check_date;
  if (typeof d !== 'string' || !ISO_DATE.test(d)) return refuse('FIRST_CHECK_DATE_FORMAT', 'The first check date must be written YYYY-MM-DD.');
  if (isoDateT(d) === null) return refuse('FIRST_CHECK_DATE_NOT_A_DATE', d + ' is not a real calendar date.');
  const dow = weekdayOf(d);
  if (dow === 0 || dow === 6) return refuse('FIRST_CHECK_DATE_WEEKEND', d + ' is a ' + WEEKDAY_NAMES[dow] + '. A check is not dated on a weekend -- enter a date a check was really paid.');
  const holiday = bankHolidayName(d);
  if (holiday) return refuse('FIRST_CHECK_DATE_HOLIDAY', d + ' is a bank holiday (' + holiday + '). A check is not dated on a holiday -- enter a date a check was really paid.');
  value.pay_first_check_date = d;
  if (!given('pay_lag_days')) return refuse('PAY_LAG_DAYS_REQUIRED', 'Enter the number of days from the end of a pay period to its check date (0 to 14).');
  const lag = b.pay_lag_days;
  if (typeof lag !== 'number' || !Number.isInteger(lag) || lag < PAY_LAG_DAYS_MIN || lag > PAY_LAG_DAYS_MAX)
    return refuse('PAY_LAG_DAYS_INVALID', 'Days from the end of a pay period to its check date must be a whole number from ' + PAY_LAG_DAYS_MIN + ' to ' + PAY_LAG_DAYS_MAX + '.');
  value.pay_lag_days = lag;
  if (!given('payroll_books')) return refuse('PAYROLL_BOOKS_REQUIRED', 'Choose how payroll is booked: accrued, or expensed when paid.');
  if (!PAYROLL_BOOKS.includes(b.payroll_books)) return refuse('PAYROLL_BOOKS_INVALID', 'Payroll books must be accrues or when_paid.');
  value.payroll_books = b.payroll_books;
  if (b.payroll_books === 'when_paid') {
    const extra = ['payroll_accrual_covers', 'accrued_payroll_bs_line_key'].filter(given);
    if (extra.length) return refuse('WHEN_PAID_EXTRA_FIELDS', 'A company that expenses payroll when paid has no ' + extra.join(', ') + ' -- leave ' + (extra.length === 1 ? 'it' : 'them') + ' blank.');
    return { value };
  }
  if (!given('payroll_accrual_covers')) return refuse('ACCRUAL_COVERS_REQUIRED', 'Choose what the payroll accrual covers: wages, or wages and employer taxes.');
  if (!PAYROLL_ACCRUAL_COVERS.includes(b.payroll_accrual_covers)) return refuse('ACCRUAL_COVERS_INVALID', 'The payroll accrual must cover wages or wages_and_taxes.');
  value.payroll_accrual_covers = b.payroll_accrual_covers;
  if (given('accrued_payroll_bs_line_key')) {
    const k = b.accrued_payroll_bs_line_key;
    if (typeof k !== 'string' || !k || k.length > 100) return refuse('ACCRUED_ACCOUNT_INVALID', 'The accrued-payroll account must be one of the accrued-liability lines on the balance sheet of this company.');
    value.accrued_payroll_bs_line_key = k;
  }
  return { value };
}
/* A3 (Brian, 2026-10-08): the accrued-payroll account picker offers EVERY balance-sheet line whose STORED Step-4 role is
   'accrued' -- any of its three types (Accrued liability / Accrued payroll / Tax payable) -- and that has a label. A stored
   role, never a name test. Takes bs_actuals_meta; returns the line keys, in the blob's own order.
   WALL MIRROR M15: server.js accruedLeafKeysFromMeta. */
export function accruedLeafKeysFromMeta(meta) {
  const out = [];
  for (const [id, m] of Object.entries(meta || {})) {
    if (!m || !m.label) continue;
    if (m.role === 'accrued') out.push(id);
  }
  return out;
}
/* THE RAISE-DAY RULE (design 5.1): the day a pay change may take effect on.
     semi-monthly, or never answered -> the 1st or the 16th (by the day digits -- exactly the test the server applies)
     bi-weekly                       -> any REAL calendar date
   Returns null when the date is allowed, else the reason: 'NOT_1ST_OR_16TH' | 'NOT_A_DATE'.
   WALL MIRROR M16: server.js raiseDayAllowed. */
export function raiseDayAllowed(payFrequency, iso) {
  if (payFrequency === 'biweekly') return isoDateT(iso) === null ? 'NOT_A_DATE' : null;
  const day = parseInt(String(iso).slice(8, 10), 10);
  return day === 1 || day === 16 ? null : 'NOT_1ST_OR_16TH';
}
